<template>
  <div class="lookup-tables-page pt-3">
    <b-row class="mb-2" align-v="center">
      <b-col cols="2">
        <b-input-group :prepend="$gettext('Search')">
          <b-form-input id="blends-name-filter" v-model="table.filters.searchName" size="lg"/>
        </b-input-group>
      </b-col>
      <b-col cols="2">
        <multiselect
          :max-height="250"
          :multiple="false"
          :clear-on-select="true"
          :hide-selected="false"
          :close-on-select="true"
          label="text"
          trackBy="value"
          :placeholder="$gettext('Component')"
          v-model="table.filters.selectedComponentsName"
          :options="searchComponentOptions"
        />
      </b-col>
      <b-col cols="2" v-if="table.filters.selectedComponentsName">
        <b-input
          v-model="table.filters.selectedComponentsPercentage"
          type="number"
          size="lg"
          placeholder="Percentage"
          min="1"
          max="100"
        />
      </b-col>
      <b-col>
        <b-btn
          v-if="table.filters.selectedComponentsName"
          variant="light"
          class="mr-3"
          @click="toggleIsComponentSortDirectionDesc"
        >
          <span v-translate>Sort by percentage</span>&nbsp;
          <i v-if="!table.filters.isComponentSortDirectionDesc" class="fa fa-arrow-up"></i>
          <i v-if="table.filters.isComponentSortDirectionDesc" class="fa fa-arrow-down"></i>
        </b-btn>
        <b-btn
          v-if="!isDisabledClearFilters"
          id="blends-clear-button"
          variant="light"
          @click="clearFilters"
        >
          <span v-translate>Clear</span>
        </b-btn>
      </b-col>
    </b-row>
    <b-table
      show-empty
      outlined
      bordered
      hover
      head-variant="light"
      stacked="md"
      :items="visibleBlends"
      :fields="tableFields"
      :current-page="table.currentPage"
      :per-page="table.perPage"
      :sort-by.sync="table.sortBy"
      :sort-desc.sync="table.sortDesc"
      :sort-direction="table.sortDirection"
      :filter="table.filters.searchName"
      @filtered="onFiltered"
      ref="table"
    >
      <template v-slot:cell(other_names)="data">
        <span v-if="data.item.other_names">{{data.item.other_names}}</span>
        <span v-else>
        </span>
      </template>
    </b-table>
    <b-pagination v-model="table.currentPage" :per-page="table.perPage" :total-rows="table.totalRows || visibleBlends.length"/>
  </div>
</template>

<script>
import './styles.css'
import Multiselect from '@/components/common/ModifiedMultiselect'

export default {
  components: {
    Multiselect
  },
  data() {
    return {
      table: {
        currentPage: 1,
        perPage: 50,
        totalRows: null,
        filters: {
          searchName: null,
          selectedComponentsName: null,
          selectedComponentsPercentage: null,
          isComponentSortDirectionDesc: true
        }
      },
      tableComponents: {
        currentPage: 1,
        perPage: Infinity
      }
    }
  },
  computed: {
    tableFields() {
      return [{
        key: 'blend_id', label: this.$gettext('Name'), sortable: true, class: 'text-left'
      }, {
        key: 'type', label: this.$gettext('Type'), class: 'text-center'
      }, {
        key: 'odp', label: this.$gettext('ODP'), class: 'text-right'
      }, {
        key: 'gwp', label: this.$gettext('GWP'), class: 'text-right'
      }, {
        key: 'composition', label: this.$gettext('Composition'), class: 'text-left'
      }, {
        key: 'other_names', label: this.$gettext('Other names'), sortable: true, class: 'text-center w-10 other-names-field'
      }, {
        key: 'trade_name', label: this.$gettext('Trade names'), sortable: true, class: 'text-center'
      }]
    },
    isDisabledClearFilters() {
      const { filters } = this.table
      return !filters.searchName && !filters.selectedComponentsName
    },
    searchComponentOptions() {
      const componentsAll = {}
      const { blends } = this.$store.state.initialData
      if (!blends) {
        return []
      }
      blends.forEach(blend => {
        blend.components.forEach(component => {
          componentsAll[component.component_name] = {
            value: component.component_name,
            text: component.component_name
          }
        })
      })
      return Object.values(componentsAll)
    },
    visibleBlends() {
      const { blends } = this.$store.state.initialData
      const { selectedComponentsName } = this.table.filters

      if (!selectedComponentsName || !blends) {
        return blends || []
      }

      return blends.filter(this.filterByComponent).sort(this.sortByComponent)
    }
  },
  methods: {
    filterByComponent(blend) {
      const component = this.getSelectedComponent(blend)
      if (!component) {
        return false
      }

      let filterPercent = this.table.filters.selectedComponentsPercentage
      if (!filterPercent) {
        return true
      }

      if (!filterPercent.includes('.')) {
        // Add a trailing decimal dot to not match 7% with 73%
        filterPercent += '.'
      }

      // Match with startsWith instead of just a number compare to allow matches like:
      //  - 15% finds 15.2%
      //  - 1.5% finds 1.58%
      //  - 21.00% finds 21%
      return (component.percentage * 100).toFixed(8).startsWith(filterPercent)
    },
    sortByComponent(blend1, blend2) {
      const p1 = this.getSelectedComponent(blend1).percentage
      const p2 = this.getSelectedComponent(blend2).percentage

      if (this.table.filters.isComponentSortDirectionDesc) {
        return p2 - p1
      }
      return p1 - p2
    },
    getSelectedComponent(blend) {
      return blend.components.find(((comp) => comp.component_name === this.table.filters.selectedComponentsName))
    },
    updateBreadcrumbs() {
      this.$store.commit('updateBreadcrumbs', `${this.$gettext('Mixtures')} | ${this.$gettext('Online Reporting System')}`)
    },
    onFiltered(filteredItems) {
      this.table.totalRows = filteredItems.length
      this.table.currentPage = 1
    },
    toggleIsComponentSortDirectionDesc() {
      this.table.filters.isComponentSortDirectionDesc = !this.table.filters.isComponentSortDirectionDesc
    },
    clearFilters() {
      const { filters } = this.table
      this.table.currentPage = 1
      filters.searchName = null
      filters.selectedComponentsName = null
      filters.isComponentSortDirectionDesc = true
      filters.selectedComponentsPercentage = null
    }
  },
  watch: {
    '$language.current': {
      handler() {
        this.updateBreadcrumbs()
      }
    }
  },
  created() {
    this.$store.dispatch('getDashboardParties')
    const body = document.querySelector('body')
    if (body.classList.contains('aside-menu-lg-show')) {
      document.querySelector('body').classList.remove('aside-menu-lg-show')
    }
    this.$store.dispatch('getCustomBlends')
    this.updateBreadcrumbs()
  }
}
</script>
<style>
  .other-names-field {
    width: 10%;
    min-width: 150px;
  }
</style>
